import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import './App.css';
import DataExplorer from './components/DataExplorer';
import Dataset1Page from './pages/Dataset1Page';
import LVCPage from './pages/LVCPage';
import LVCScenarioPage from './pages/LVCScenarioPage';
import LVCAgentPage from './pages/LVCAgentPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import RecordedDayMapPage from './pages/RecordedDayMapPage';
import ToolsPage from './pages/ToolsPage';
import GeoJsonGpxPage from './pages/GeoJsonGpxPage';
import SkiAreaDagPage from './pages/SkiAreaDagPage';
import UIKitPage from './pages/UIKitPage';
import SerialMonitorPage from './pages/SerialMonitorPage';
import OTAManagementPage from './pages/OTAManagementPage';
import FactoryFlashPage from './pages/FactoryFlashPage';
import QAValidationPage from './pages/QAValidationPage';
import AnalyticsPage from './pages/AnalyticsPage';
import VirtualGogglesPage from './pages/VirtualGogglesPage';
import IntelligencePage from './pages/IntelligencePage';
import MapManagementPage from './pages/MapManagementPage';
import MapImportPage from './pages/MapImportPage';
import MapVersionHistoryPage from './pages/MapVersionHistoryPage';
import MapViewerPage from './pages/MapViewerPage';
import MapFeedbackPage from './pages/MapFeedbackPage';
import ResortLiveMapsPage from './pages/ResortLiveMapsPage';
import ReleaseManagementPage from './pages/ReleaseManagementPage';
import RekkieSnowPage from '../products/rekkie-snow/frontend/RekkieSnowPage';
import Dropdown from './components/Dropdown';
import ProfileMenu from './components/ProfileMenu';
import Login from './components/Login';
import ErrorNotification from './components/ErrorNotification';
import McpKeysPanel from './components/McpKeysPanel';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';

// Map tab IDs to their routes (static, defined outside component)
const TAB_ROUTE_MAP = {
  'data-explorer': '/',
  'lvc': '/lvc',
  'serial-monitor': '/serial-monitor',
  'ota-management': '/ota-management',
  'factory-flash': '/factory-flash',
  'qa-validation': '/qa-validation',
  'map-management': '/map-management',
  'release-management': '/release-management',
  'rekkie-snow': '/rekkie-snow',
  'tools': '/tools',
  'users': '/users',
  'settings': '/settings',
  'analytics': '/analytics'
};

/**
 * Protected Route Component
 * Redirects to home if user doesn't have access to the required product
 */
const ProtectedRoute = ({ children, product }) => {
  const { hasAccess } = useAuth();
  
  // If no product is required, allow access
  if (!product) {
    return children;
  }
  
  // Check if user has access to the product
  if (!hasAccess(product)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AppContent = () => {
  const { currentUser, loading, logout, accessLoading, hasAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isDatasetPage = location.pathname.startsWith('/dataset/');
  const isLVCAgentPage = location.pathname.includes('/lvc/') && location.pathname.includes('/agent/');
  const isLVCScenarioPage = location.pathname.startsWith('/lvc/') && location.pathname !== '/lvc' && !isLVCAgentPage;
  const isLVCPage = location.pathname === '/lvc';
  const isUserDetailPage = location.pathname.startsWith('/users/') && location.pathname !== '/users';
  const isGeoJsonGpxPage = location.pathname === '/tools/geojson-gpx';
  const isSkiAreaDagPage = location.pathname === '/tools/ski-area-dag';
  const isUIKitPage = location.pathname === '/tools/uikit';
  const isSerialMonitorPage = location.pathname === '/serial-monitor';
  const isOTAManagementPage = location.pathname === '/ota-management';
  const isFactoryFlashPage = location.pathname === '/factory-flash';
  const isAnalyticsPage = location.pathname.startsWith('/analytics');
  const isVirtualGogglesPage = location.pathname === '/tools/virtual-goggles';
  const isMapManagementPage = location.pathname.startsWith('/map-management');
  const isReleaseManagementPage = location.pathname.startsWith('/release-management');
  const isRekkieSnowPage = location.pathname === '/rekkie-snow' || location.pathname.startsWith('/rekkie-snow/');
  
  const [activeTab, setActiveTab] = useState('data-explorer');

  // Define all tabs with their product requirements (needed before routing logic)
  const allTabs = [
    { id: 'data-explorer', label: 'Data Explorer', icon: '/data_explorer.png', product: 'datasets' },
    { id: 'lvc', label: 'Live Virtual Constructive (LVC)', icon: '/App_icon_1024.png', product: 'lvc' },
    { id: 'serial-monitor', label: 'Serial Monitor', icon: '/App_icon_1024.png', product: 'serial_monitor' },
    { id: 'ota-management', label: 'OTA Management', icon: '/App_icon_1024.png', product: null }, // Accessible to all authenticated users
    { id: 'factory-flash', label: 'Factory Flash', icon: '/App_icon_1024.png', product: 'factory_flash' },
    { id: 'qa-validation', label: 'QA Validation', icon: '/App_icon_1024.png', product: 'qa' }, // REKKIE Eval & Test-Suites
    { id: 'map-management', label: 'Map Management', icon: '/App_icon_1024.png', product: 'mapManagement' },
    { id: 'rekkie-snow', label: 'REKKIE Snow', icon: '/App_icon_1024.png', product: 'rekkie_snow' },
    { id: 'release-management', label: 'Release Management', icon: '/App_icon_1024.png', product: 'release_management' },
    { id: 'tools', label: 'Tools', icon: '/App_icon_1024.png', product: 'tools' },
    { id: 'analytics', label: 'Analytics', icon: '/App_icon_1024.png', product: 'analytics' },
    { id: 'users', label: 'Users', icon: '/App_icon_1024.png', product: 'users' },
    { id: 'settings', label: 'Settings', icon: '/App_icon_1024.png', product: null }, // No product requirement
  ];

  // Filter tabs based on user access
  const tabs = allTabs.filter(tab => {
    // If tab has no product requirement, show it to everyone
    if (!tab.product) return true;
    // Check if user has access to the product
    return hasAccess(tab.product);
  });

  // Redirect to first accessible tab if user lands on home without access to data-explorer
  useEffect(() => {
    // Only redirect if on home page and user doesn't have access to datasets (data-explorer)
    if (location.pathname === '/' && !hasAccess('datasets') && tabs.length > 0) {
      const firstAccessibleTab = tabs[0];
      const targetRoute = TAB_ROUTE_MAP[firstAccessibleTab.id];
      if (targetRoute && targetRoute !== '/') {
        navigate(targetRoute, { replace: true });
      }
    }
  }, [location.pathname, hasAccess, tabs, navigate]);

  // Update active tab based on current route - must be called before any conditional returns
  useEffect(() => {
    if (isLVCPage || isLVCScenarioPage || isLVCAgentPage) {
      setActiveTab('lvc');
    } else if (isDatasetPage) {
      setActiveTab('data-explorer');
    } else if (location.pathname === '/users' || isUserDetailPage) {
      setActiveTab('users');
    } else if (location.pathname.startsWith('/tools')) {
      setActiveTab('tools');
    } else if (location.pathname === '/qa-validation') {
      setActiveTab('qa-validation');
    } else if (location.pathname.startsWith('/map-management')) {
      setActiveTab('map-management');
    } else if (isReleaseManagementPage) {
      setActiveTab('release-management');
    } else if (isRekkieSnowPage) {
      setActiveTab('rekkie-snow');
    } else if (isSerialMonitorPage) {
      setActiveTab('serial-monitor');
    } else if (isOTAManagementPage) {
      setActiveTab('ota-management');
    } else if (isFactoryFlashPage) {
      setActiveTab('factory-flash');
    } else if (location.pathname.startsWith('/analytics')) {
      setActiveTab('analytics');
    } else if (location.pathname === '/settings') {
      setActiveTab('settings');
    } else {
      setActiveTab('data-explorer');
    }
  }, [location.pathname, isLVCPage, isLVCScenarioPage, isLVCAgentPage, isDatasetPage, isUserDetailPage, isSerialMonitorPage, isOTAManagementPage, isFactoryFlashPage, isAnalyticsPage, isReleaseManagementPage, isRekkieSnowPage]);

  // Show loading screen while checking authentication or access
  if (loading || accessLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login if not authenticated - block all access
  if (!currentUser) {
    return <Login />;
  }

  // Get icon based on active tab
  const selectedTab = tabs.find(tab => tab.id === activeTab);
  const currentIcon = selectedTab?.icon || '/App_icon_1024.png';

  const renderTabContent = () => {
    switch (activeTab) {
      case 'data-explorer':
        if (!hasAccess('datasets')) {
          return (
            <div className="tab-content">
              <h2>Access Denied</h2>
              <p>You do not have access to Data Explorer. Contact an administrator for access.</p>
            </div>
          );
        }
        return <DataExplorer />;
      case 'tools':
        return <ToolsPage />;
      case 'users':
        if (!hasAccess('users')) {
          return (
            <div className="tab-content">
              <h2>Access Denied</h2>
              <p>You do not have access to Users. Contact an administrator for access.</p>
            </div>
          );
        }
        return <UsersPage />;
      case 'qa-validation':
        if (!hasAccess('qa')) {
          return (
            <div className="tab-content">
              <h2>Access Denied</h2>
              <p>You do not have access to QA Validation. Contact an administrator for access.</p>
            </div>
          );
        }
        return <QAValidationPage />;
      case 'settings':
        return (
          <div className="tab-content">
            <h2>Settings</h2>
            <McpKeysPanel />
          </div>
        );
      default:
        return <DataExplorer />;
    }
  };

  // Handle tab selection and navigation
  const handleTabSelect = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'lvc') {
      navigate('/lvc');
    } else if (tabId === 'data-explorer') {
      navigate('/');
    } else if (tabId === 'tools') {
      navigate('/tools');
    } else if (tabId === 'users') {
      navigate('/users');
    } else if (tabId === 'qa-validation') {
      navigate('/qa-validation');
    } else if (tabId === 'map-management') {
      navigate('/map-management');
    } else if (tabId === 'release-management') {
      navigate('/release-management');
    } else if (tabId === 'rekkie-snow') {
      navigate('/rekkie-snow');
    } else if (tabId === 'serial-monitor') {
      navigate('/serial-monitor');
    } else if (tabId === 'ota-management') {
      navigate('/ota-management');
    } else if (tabId === 'factory-flash') {
      navigate('/factory-flash');
    } else if (tabId === 'analytics') {
      navigate('/analytics');
    } else if (tabId === 'settings') {
      navigate('/settings');
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-content">
          <Dropdown
            options={tabs}
            selected={activeTab}
            onSelect={handleTabSelect}
            icon={currentIcon}
          />
          <div className="app-header-user">
            <ErrorNotification />
            <ProfileMenu user={currentUser} onSignOut={logout} />
          </div>
        </div>
      </header>

      <main className={isDatasetPage || isLVCScenarioPage || isLVCAgentPage || isUserDetailPage || isGeoJsonGpxPage || isSkiAreaDagPage || isUIKitPage || isSerialMonitorPage || isOTAManagementPage || isFactoryFlashPage || isAnalyticsPage || isMapManagementPage || isReleaseManagementPage || isRekkieSnowPage || isVirtualGogglesPage ? 'app-main-full' : 'app-main'}>
        <Routes>
          <Route path="/dataset/:datasetId" element={
            <ProtectedRoute product="datasets">
              <Dataset1Page />
            </ProtectedRoute>
          } />
          <Route path="/lvc/new" element={
            <ProtectedRoute product="lvc">
              <LVCScenarioPage />
            </ProtectedRoute>
          } />
          <Route path="/lvc/:scenarioId/agent/:agentIndex" element={
            <ProtectedRoute product="lvc">
              <LVCAgentPage />
            </ProtectedRoute>
          } />
          <Route path="/lvc/:scenarioId" element={
            <ProtectedRoute product="lvc">
              <LVCScenarioPage />
            </ProtectedRoute>
          } />
          <Route path="/lvc" element={
            <ProtectedRoute product="lvc">
              <LVCPage />
            </ProtectedRoute>
          } />
          <Route path="/tools/geojson-gpx" element={
            <ProtectedRoute product="tools">
              <GeoJsonGpxPage />
            </ProtectedRoute>
          } />
          <Route path="/tools/ski-area-dag" element={
            <ProtectedRoute product="tools">
              <SkiAreaDagPage />
            </ProtectedRoute>
          } />
          <Route path="/tools/uikit" element={
            <ProtectedRoute product="tools">
              <UIKitPage />
            </ProtectedRoute>
          } />
          <Route path="/tools/virtual-goggles" element={
            <ProtectedRoute product="tools">
              <VirtualGogglesPage />
            </ProtectedRoute>
          } />
          <Route path="/tools/intelligence" element={
            <ProtectedRoute product="tools">
              <IntelligencePage />
            </ProtectedRoute>
          } />
          <Route path="/tools" element={
            <ProtectedRoute product="tools">
              <ToolsPage />
            </ProtectedRoute>
          } />
          <Route path="/serial-monitor" element={
            <ProtectedRoute product="serial_monitor">
              <SerialMonitorPage />
            </ProtectedRoute>
          } />
          <Route path="/ota-management" element={<OTAManagementPage />} />
          <Route path="/factory-flash" element={
            <ProtectedRoute product="factory_flash">
              <FactoryFlashPage />
            </ProtectedRoute>
          } />
          <Route path="/users/:userId/recorded-days/:day/map" element={
            <ProtectedRoute product="users">
              <RecordedDayMapPage />
            </ProtectedRoute>
          } />
          <Route path="/users/:userId" element={
            <ProtectedRoute product="users">
              <UserDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute product="users">
              <UsersPage />
            </ProtectedRoute>
          } />
          <Route path="/qa-validation" element={
            <ProtectedRoute product="qa">
              <QAValidationPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute product="analytics">
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/map-management" element={
            <ProtectedRoute product="mapManagement">
              <MapManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/map-management/import" element={
            <ProtectedRoute product="mapManagement">
              <MapImportPage />
            </ProtectedRoute>
          } />
          <Route path="/map-management/versions" element={
            <ProtectedRoute product="mapManagement">
              <MapVersionHistoryPage />
            </ProtectedRoute>
          } />
          <Route path="/map-management/viewer" element={
            <ProtectedRoute product="mapManagement">
              <MapViewerPage />
            </ProtectedRoute>
          } />
          <Route path="/map-management/feedback" element={
            <ProtectedRoute product="mapManagement">
              <MapFeedbackPage />
            </ProtectedRoute>
          } />
          <Route path="/map-management/resort-live-maps" element={
            <ProtectedRoute product="mapManagement">
              <ResortLiveMapsPage />
            </ProtectedRoute>
          } />
          <Route path="/release-management" element={
            <ProtectedRoute product="release_management">
              <ReleaseManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
          <Route path="/rekkie-snow" element={
            <ProtectedRoute product="rekkie_snow">
              <RekkieSnowPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <div className="tab-content">
              <h2>Settings</h2>
              <McpKeysPanel />
            </div>
          } />
          <Route path="/" element={renderTabContent()} />
        </Routes>
      </main>
    </div>
  );
};

const App = () => {
  return <AppContent />;
};

export default App;

